import React, { useState, useEffect } from 'react';
import clsx from 'clsx';
import { makeStyles } from 'tss-react/mui';
import { keyframes } from "tss-react";
import {
  AppBar,
  Toolbar,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  ExitToApp as LogoutIcon,
  Settings as SettingsIcon,
  InfoOutlined as AboutIcon
} from '@mui/icons-material';
import ProfileDialog from './ProfileDialog';
import AboutDialog from './AboutDialog';
import { resolveAvatarUrl } from '../services/api';
import AvatarFallback from './AvatarFallback';

const seshHitFade = keyframes`
  0% { opacity: 1; }
  100% { opacity: 0; }
`;

const useStyles = makeStyles()((theme) => ({
  appBar: {
    backgroundColor: theme.palette.custom.panelRaised,
    boxShadow: `0 1px 0 ${theme.palette.custom.line}`,
    zIndex: theme.zIndex.drawer + 1,
    flexShrink: 0,
  },
  toolbar: {
    minHeight: 56,
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    width: '100%',
  },
  leftSection: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  logo: {
    height: 56,
    width: 'auto',
    display: 'block',
  },
  userAvatar: {
    width: 30,
    height: 30,
  },
  // Custom photo/builtin avatars paint their own pixels — drop the colored
  // fallback fill behind them. '&&' doubles the class specificity so this
  // deterministically beats MUI's .MuiAvatar-colorDefault regardless of
  // emotion injection order (sx overrides proved order-dependent).
  userAvatarPhoto: {
    '&&': { background: 'transparent' },
  },
  // User identity chip — rounded pill (name + avatar), adapted from mockup `.me`.
  userChip: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.25),
    padding: '5px 6px 5px 12px',
    border: `1px solid ${theme.palette.custom.line}`,
    borderRadius: 99,
    backgroundColor: 'rgba(255,255,255,0.02)',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.custom.hoverOverlay,
    },
  },
  userChipName: {
    color: theme.palette.text.primary,
    fontWeight: 600,
    fontSize: '0.8125rem',
  },
  rightSection: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: theme.spacing(2),
  },
  rightSectionWithSeshStatus: {
    flex: 0,
  },
  // Center zone holds the sesh button; flex:1 on both side zones keeps it
  // truly centered regardless of how wide the username renders.
  centerSection: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  // ---- THE SESH BUTTON (centerpiece) ----
  // Ember-gradient rounded pill with a pulsing ring, lifted from the mockup's
  // `.sesh-btn`. Theme-aware: gradient + glow read from palette.custom.ember/*.
  seshWrap: {
    display: 'flex',
    alignItems: 'center',
  },
  seshBtn: {
    position: 'relative',
    border: 0,
    background: 'transparent',
    padding: 0,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    lineHeight: 0, // no extra box around the image
    transition: 'transform 0.14s ease, filter 0.15s ease',
    '&:hover': {
      transform: 'translateY(-1px)',
      filter: 'brightness(1.06)',
    },
    '&:active': { transform: 'translateY(0) scale(0.98)' },
    // Disabled (guests, or a sesh already live): flat + non-interactive.
    '&:disabled': {
      cursor: 'default',
      opacity: 0.55,
      transform: 'none',
      filter: 'grayscale(0.25)',
    },
  },
  // The button face — the pre-rendered button.png (already labelled + styled).
  seshImg: {
    display: 'block',
    height: 49,
    width: 'auto',
  },
  // The "Klar" join button (klar.png) — sits between the logo and the status
  // copy, offered to users not yet in the sesh.
  klarBtn: {
    border: 0,
    background: 'transparent',
    padding: 0,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    lineHeight: 0,
    flexShrink: 0,
    transition: 'transform 0.14s ease, filter 0.15s ease',
    '&:hover': { transform: 'translateY(-1px)', filter: 'brightness(1.08)' },
    '&:active': { transform: 'translateY(0) scale(0.96)' },
  },
  klarImg: {
    display: 'block',
    height: 49,
    width: 'auto',
  },
  // ---- Inline live-sesh status (right of the logo) ----
  // Replaces the start button while a sesh is live. Two stacked lines: the
  // "klar" headline + hit clock, and the smokers list.
  seshStatus: {
    flex: 1, // take the remaining bar width (the center start button is hidden)
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    gap: 2,
    paddingLeft: theme.spacing(1.5),
    marginLeft: theme.spacing(0.5),
    borderLeft: `1px solid ${theme.palette.custom.line}`,
    minWidth: 0, // allow ellipsis on the smokers line
  },
  '@keyframes seshHitFade': {
    '0%': { opacity: 1 },
    '100%': { opacity: 0 },
  },
  blink: {
    // animation: `${seshHitFade} 0.8s ease-out 5 forwards`,
  },
  seshStatusLine: {
    fontFamily: theme.palette.custom.fontFamily,
    fontWeight: 700,
    fontSize: '0.85rem',
    lineHeight: 1.2,
    color: theme.palette.custom.ember,
    // The headline is the point — keep it fully visible. It's long, so allow it
    // to wrap (the toolbar's minHeight lets the bar grow). No nowrap/ellipsis.
  },
  // "Take a hit!" — fade the headline from full opacity to 0, then repeat,
  // ~5 times (the legacy button.php faded #smoketext out on each poll, which
  // read as a repeated fade-out). Sawtooth (snap to 1, fade to 0) over 5 cycles
  // ≈ 4s, comfortably inside the done window (SESH_HIT_CLEAR_MS = 5500ms);
  // `forwards` leaves it at the final keyframe (faded) once done.
  seshStatusHit: {
    '& $seshStatusLine': {
      animation: `${seshHitFade} 0.8s ease-out 5 forwards`,
    },
  },
  // The (HH:MM:SS) wall-clock time of the hit — quieter, monospace.
  seshHitClock: {
    fontFamily: theme.palette.custom.fontMono,
    fontWeight: 700,
    color: theme.palette.custom.inkDim,
  },
  seshSmokers: {
    fontSize: '0.72rem',
    color: theme.palette.custom.inkDim,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: 320,
  },
  seshSmokersNames: {
    color: theme.palette.custom.leaf,
    fontWeight: 600,
  },
  menuItem: {
    fontSize: '0.875rem',
    '& .MuiListItemIcon-root': {
      minWidth: 36,
    },
  },
  statusIcon: {
    fontSize: '0.875rem',
    marginRight: theme.spacing(1),
  },
}));

const TopBar = ({
  user = { id: '227288', username: 'entropic_22', avatar: '/upload/thumb/227288.jpg' },
  roomName = 'chat',
  onMenuClick,
  onLogout,
  role,
  isGuest = false,
  displayName,
  usernameColor,
  canPickUsernameColor = false,
  canUseReservedColor = false,
  onUsernameColorChange,
  outputQuality,
  canPickOutputQuality = false,
  onOutputQualityChange,
  themeName,
  onThemeChange,
  mentionSound,
  onMentionSoundChange,
  mentionVolume,
  onMentionVolumeChange,
  onMentionVolumePreview,
  fontSize,
  onFontSizeChange,
  timestampFormat,
  onTimestampFormatChange,
  imMode,
  onImModeChange,
  imClassic,
  onImClassicChange,
  awayMessage,
  onAwayChange,
  deviceSetup,
  onDeviceSetupChange,
  // Vocal chain (Settings → Voice & Video → Microphone processing) — passed
  // straight through to the profile dialog. isPublishing switches its meter
  // between the live publish chain and a temporary preview capture.
  micProcessing,
  onMicProcessingChange,
  isPublishing = false,
  onAvatarChange,
  onAvatarPick,
  // Profile fields (per-tenant) — passed straight through to the profile dialog's
  // "My Account" bio + banner-color editors.
  bio = '',
  onBioChange,
  bannerColor = '',
  onBannerColorChange,
  // Quick Replies — passed straight through to the profile dialog's
  // "Quick Replies" management pane.
  quickReplies = [],
  onQuickRepliesChange,
  // --- Group "Sesh" Countdown (the centerpiece) ---
  // canStartSesh: members only (guests can join but not start — App computes
  // this and the SERVER enforces it; the disabled button is cosmetic).
  // sesh: the live server snapshot, or null when no sesh is active. When set,
  // the start button hides for everyone and an inline status renders next to
  // the logo. Shape: { phase, secondsLeft, total, smokers:[{userId,username}],
  // initiator:{userId,username} }.
  canStartSesh = false,
  currentUserId,
  sesh = null,
  // True once the join ack has resolved whether a sesh is active. Until then we
  // don't render the start button, so a reload mid-sesh never flashes the
  // button before the status appears.
  seshSynced = false,
  onStartSesh,
  onJoinSesh
}) => {
  const seshActive = !!sesh;
  // On logout the parent passes user={null} for a render before unmounting;
  // default params only cover undefined, so guard against null here.
  const safeUser = user || {};
  // Canonical display name: the membership username (passed by the parent) wins,
  // since authData.user carries only id/email/displayName — the username lives on
  // the membership. Fall back to anything on the user object for safety.
  const shownName =
    displayName || safeUser.displayName || safeUser.username || 'You';
  // The avatar can arrive as `avatar` (room/socket payloads, live updates) or
  // `avatarUrl` (the auth payload's user object on first load). Resolve uploaded
  // paths to the backend origin; no avatar / the server's placeholder resolves
  // to undefined so the theme-tinted builtin default (AvatarFallback) shows.
  const resolvedAvatar = resolveAvatarUrl(safeUser.avatar || safeUser.avatarUrl);
  const { classes, cx } = useStyles();
  const [anchorEl, setAnchorEl] = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);

  // Preload the sesh button images (button.png / klar.png) once on mount so
  // they're decoded and cached before we render them — otherwise the <img> tag
  // mounts, then fetches, giving a flash of empty/broken image. We only flip the
  // start button / klar button on once their image is ready. The status text is
  // not gated (it has no image). Decoding both up front means a sesh that starts
  // later shows klar.png instantly too.
  const [imagesReady, setImagesReady] = useState(false);
  
  useEffect(() => {
    const base = process.env.PUBLIC_URL || '';
    const srcs = [`${base}/button.png`, `${base}/klar.png`];
    let cancelled = false;
    const settled = new Set();
    const settle = (src) => {
      if (settled.has(src)) return; // count each image at most once
      settled.add(src);
      if (!cancelled && settled.size >= srcs.length) setImagesReady(true);
    };
    srcs.forEach((src) => {
      const img = new Image();
      img.onload = () => settle(src);
      img.onerror = () => settle(src); // don't hang forever if one 404s
      img.src = src;
      // A cached image may already be complete before onload can fire.
      if (img.complete) settle(src);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleProfileOpen = () => {
    handleMenuClose();
    setProfileOpen(true);
  };

  const handleAboutOpen = () => {
    handleMenuClose();
    setAboutOpen(true);
  };

  // ---- Inline sesh status (rendered next to the logo while a sesh is live) ----
  let seshStatus = null;
  if (seshActive) {
    const secondsLeft = typeof sesh.secondsLeft === 'number' ? sesh.secondsLeft : 0;
    const isDone = sesh.phase === 'done';
    // The hit clock is the server's LOCKED smokeAt (absolute ms epoch), formatted
    // as-is — never recomputed from secondsLeft. This is what stops the ±1s drift
    // (06:44:44 → 06:44:43): the displayed time is fixed for the life of the sesh.
    const hitClock =
      typeof sesh.smokeAt === 'number'
        ? new Date(sesh.smokeAt).toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
          })
        : '';
    // Legacy flavor prefix (420 / coast / noodle), computed server-side once and
    // sent in the snapshot. "***NOODLE***" / "*East Coast 420*" style, matching
    // button.php's button_type.
    let flavor = '';
    if (sesh.isNoodle) {
      flavor = sesh.label ? `***${sesh.label} NOODLE*** ` : '***NOODLE*** ';
    } else if (sesh.label) {
      flavor = `*${sesh.label}* `;
    }
    const headline = isDone
      ? 'Take a hit! 🔥'
      : `${flavor}Fyld up your heads and get klar to take a hit in ${secondsLeft} seconds!`;
    const smokers = sesh.smokers || [];
    const names = smokers.map((s) => s.username).filter(Boolean);
    // Already in the sesh? Joins are only accepted during the JOIN window
    // (server-enforced), so only offer the klar button then, and only if we
    // haven't joined yet. Guests can join (just can't start) — no gate here.
    const joined = smokers.some((s) => s.userId === currentUserId);
    // Gate the klar button on imagesReady too — the status text shows
    // immediately, but the klar.png affordance waits until its image is cached.
    const canJoin = sesh.phase === 'join' && !joined && !!onJoinSesh && imagesReady;
    seshStatus = { headline, hitClock, names, isDone, canJoin };
  }

  return (
    <AppBar position="static" className={classes.appBar}>
      <Toolbar className={classes.toolbar}>
        <div className={classes.leftSection}>
          {/* Brand logo */}
          <img
            src={`${process.env.PUBLIC_URL}/CC-logo.png`}
            alt="ChatCove"
            className={classes.logo}
          />

          {/* Klar join button — shown to a user not yet in the sesh, between
              the logo and the status copy. Clicking joins them; the smokers
              broadcast then flips `canJoin` false and this button disappears,
              letting the status copy shift left into its place. */}
          {seshStatus && seshStatus.canJoin && (
            <button
              type="button"
              className={classes.klarBtn}
              onClick={onJoinSesh}
              aria-label="Join the sesh — I'm klar"
              title="I'm klar — join the sesh"
            >
              <img
                src={`${process.env.PUBLIC_URL}/klar.png`}
                alt="Klar — join the sesh"
                className={classes.klarImg}
                draggable={false}
              />
            </button>
          )}

          {/* Live sesh status — shows next to the logo for everyone in the room
              while a sesh is active (the center start button hides). */}
          {seshStatus && (
            <div
              className={`${classes.seshStatus} ${seshStatus.isDone ? classes.seshStatusHit : ''}`}
              role="status"
              aria-live="polite"
            >
              <span className={clsx(classes.seshStatusLine, seshStatus.isDone && classes.blink)}>
                {seshStatus.headline}
                {!seshStatus.isDone && (
                  <span className={classes.seshHitClock}> ({seshStatus.hitClock})</span>
                )}
              </span>
              {seshStatus && !seshStatus.isDone && (
                <span className={classes.seshSmokers}>
                  Smokers who are klar:{' '}
                  <span className={classes.seshSmokersNames}>
                    {seshStatus.names.length ? seshStatus.names.join(', ') : '—'}
                  </span>
                
                </span>
              )}
            </div>
          )}
        </div>

        {/* Centerpiece: "Start smoke countdown" (the button.png face). Dead-
            center via the flex:1 side zones. While a sesh is live the button is
            hidden for EVERYONE (the inline status by the logo takes over);
            disabled (but visible) for guests, who can't initiate. */}
            {!seshActive && (
        <div className={classes.centerSection}>
          {onStartSesh && !seshActive && seshSynced && imagesReady && (
            <div className={classes.seshWrap}>
              <button
                type="button"
                className={classes.seshBtn}
                onClick={onStartSesh}
                disabled={!canStartSesh}
                aria-label="Start a group smoke countdown"
                title={
                  canStartSesh ? 'Start smoke countdown' : 'Only members can start a sesh'
                }
              >
                {/* The pre-rendered button.png IS the button face. */}
                <img
                  src={`${process.env.PUBLIC_URL}/button.png`}
                  alt="Start smoke countdown"
                  className={classes.seshImg}
                  draggable={false}
                />
              </button>
            </div>
          )}
        </div>
            )}

        <div className={clsx(classes.rightSection, seshActive && classes.rightSectionWithSeshStatus)}>
          {/* User identity chip — name + avatar, far right (mockup `.me`) */}
          <div className={classes.userChip} onClick={handleMenuOpen}>
            <Typography className={classes.userChipName}>
              {shownName}
            </Typography>
            <Avatar
              src={resolvedAvatar}
              alt={safeUser.username}
              className={cx(
                classes.userAvatar,
                // Always transparent behind the image — the fallback is itself
                // an image now (the theme-tinted builtin default).
                classes.userAvatarPhoto
              )}
              sx={{ color: usernameColor }}
            >
              <AvatarFallback letter={(shownName || '?').charAt(0).toUpperCase()} />
            </Avatar>
          </div>
        </div>

        {/* User Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          getContentAnchorEl={null}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
        >
          <MenuItem onClick={handleProfileOpen} className={classes.menuItem}>
            <ListItemIcon>
              <SettingsIcon className={classes.statusIcon} />
            </ListItemIcon>
            <ListItemText primary="Settings" />
          </MenuItem>
          <MenuItem onClick={handleAboutOpen} className={classes.menuItem}>
            <ListItemIcon>
              <AboutIcon className={classes.statusIcon} />
            </ListItemIcon>
            <ListItemText primary="About" />
          </MenuItem>
          <MenuItem onClick={onLogout} className={classes.menuItem}>
            <ListItemIcon>
              <LogoutIcon className={classes.statusIcon} />
            </ListItemIcon>
            <ListItemText primary="Logout" />
          </MenuItem>
        </Menu>

        <AboutDialog open={aboutOpen} onClose={() => setAboutOpen(false)} />

        <ProfileDialog
          open={profileOpen}
          onClose={() => setProfileOpen(false)}
          user={{ ...safeUser, avatar: resolvedAvatar }}
          onAvatarChange={onAvatarChange}
          onAvatarPick={onAvatarPick}
          rawAvatar={safeUser.avatar || safeUser.avatarUrl}
          bio={bio}
          onBioChange={onBioChange}
          bannerColor={bannerColor}
          onBannerColorChange={onBannerColorChange}
          role={role}
          isGuest={isGuest}
          username={shownName}
          usernameColor={usernameColor}
          canPick={canPickUsernameColor}
          canUseReserved={canUseReservedColor}
          onUsernameColorChange={onUsernameColorChange}
          outputQuality={outputQuality}
          canPickOutputQuality={canPickOutputQuality}
          onOutputQualityChange={onOutputQualityChange}
          themeName={themeName}
          onThemeChange={onThemeChange}
          mentionSound={mentionSound}
          onMentionSoundChange={onMentionSoundChange}
          mentionVolume={mentionVolume}
          onMentionVolumeChange={onMentionVolumeChange}
          onMentionVolumePreview={onMentionVolumePreview}
          fontSize={fontSize}
          onFontSizeChange={onFontSizeChange}
          timestampFormat={timestampFormat}
          onTimestampFormatChange={onTimestampFormatChange}
          imMode={imMode}
          onImModeChange={onImModeChange}
          imClassic={imClassic}
          onImClassicChange={onImClassicChange}
          awayMessage={awayMessage}
          onAwayChange={onAwayChange}
          deviceSetup={deviceSetup}
          onDeviceSetupChange={onDeviceSetupChange}
          micProcessing={micProcessing}
          onMicProcessingChange={onMicProcessingChange}
          isPublishing={isPublishing}
          quickReplies={quickReplies}
          onQuickRepliesChange={onQuickRepliesChange}
        />
      </Toolbar>
    </AppBar>
  );
};

export default TopBar;